import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Icon } from '@design-manager-inc/ui';
import 'app/App.css';
import 'react-toastify/dist/ReactToastify.css';
import { routes } from 'app/features/Routing/Routes';
import { useTheme } from 'contexts/ThemeContext';
import 'app/styles/fonts.css';
import { useAuth } from 'app/features/auth/useAuth';
import { ToastContainer } from 'react-toastify';
import { NavItemsWrapper } from './partials/NavItemsWrapper';
import { useMixpanelIdentify } from '../Analytics/hooks/useMixpanelIdentify';
import { Logo } from './partials/Logo';
import { EFeatureFlags } from 'app/features/FeatureFlags/featureFlags';
import { useFeature } from 'app/features/FeatureFlags/useFeature';

export interface INavItem {
	name: string;
	icon: string | undefined;
	link: string;
}

export const Layout = () => {
	const { theme } = useTheme();
	const [searchParams] = useSearchParams();
	const token = searchParams.get('token') || '';
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	const [imageError, setImageError] = useState(false);
	const { org, isAuthenticated } = useAuth();
	const navigate = useNavigate();
	const { isFeatureEnabled: showMoodBoards } = useFeature(
		EFeatureFlags.MoodBoards,
	);

	useMixpanelIdentify();

	const logoSrc = useMemo(
		() =>
			org?.branding?.logoFileId
				? `${process.env.PUBLIC_APP_API_BASE_URL}/file/${token}/${org.branding.logoFileId}`
				: '',
		[org?.branding?.logoFileId, token],
	);

	useEffect(() => {
		const primaryColor = org?.branding?.primary || '#f3f4f6';
		const secondaryColor = org?.branding?.secondary || '#3338A1';

		document.documentElement.style.setProperty('--primary-color', primaryColor);
		document.documentElement.style.setProperty(
			'--secondary-color',
			secondaryColor,
		);
	}, [org?.branding]);

	const navItems = useMemo<INavItem[]>(
		() => [
			{
				name: routes.dashboard.name,
				icon: routes.dashboard.icon,
				link: `${routes.dashboard.path}?token=${token}`,
			},
			...(showMoodBoards
				? [
						{
							name: routes.moodboards.name,
							icon: routes.moodboards.icon,
							link: `${routes.moodboards.path}?token=${token}`,
						},
					]
				: []),
			{
				name: routes.items.name,
				icon: routes.items.icon,
				link: `${routes.items.path}?token=${token}`,
			},
			{
				name: routes.proposals.name,
				icon: routes.proposals.icon,
				link: `${routes.proposals.path}?token=${token}`,
			},
			{
				name: routes.invoices.name,
				icon: routes.invoices.icon,
				link: `${routes.invoices.path}?token=${token}`,
			},
		],
		[token, showMoodBoards],
	);

	return (
		<div
			className={`${theme === 'dark' ? 'dark' : ''} flex flex-col h-screen md:h-fit lg:h-fit bg-background text-text`}
			data-environment={process.env.PUBLIC_ENVIRONMENT_NAME}
		>
			{isAuthenticated ? (
				<>
					{/* Mobile Header */}
					<header className="md:hidden border-b border-gray-400 shadow-lg rounded-lg p-4 flex justify-between items-center sticky top-0 bg-background z-50">
						<div className="flex flex-row gap-2 items-center">
							<Logo
								src={logoSrc}
								alt={org?.name || ''}
								imageError={imageError}
								onError={() => setImageError(true)}
								onClick={() => navigate(`/dashboard?token=${token}`)}
							/>
							<span className="font-semibold text-primary-badge">
								{org?.name}
							</span>
						</div>
						<Button
							variant="ghost"
							onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
						>
							<Icon name="bars-2" className="fill-primary-badge" />
						</Button>
					</header>

					{/* Mobile Menu */}
					{isMobileMenuOpen && (
						<div className="h-[550px] md:hidden fixed shadow-lg rounded-b-2xl inset-0 z-50 bg-background">
							<div className="flex flex-col h-full">
								<div className="flex justify-between items-center p-4 border-b border-border">
									<div className="flex items-center gap-4">
										<Logo
											src={logoSrc}
											alt={org?.name || ''}
											imageError={imageError}
											onError={() => setImageError(true)}
											onClick={() => navigate(`/dashboard?token=${token}`)}
										/>
										<span className="font-semibold text-primary-badge">
											{org?.name}
										</span>
									</div>
									<Button
										variant="ghost"
										onClick={() => setIsMobileMenuOpen(false)}
									>
										<Icon name="x-mark" className="fill-primary-badge" />
									</Button>
								</div>
								<NavItemsWrapper
									navItems={navItems}
									setIsMobileMenuOpen={setIsMobileMenuOpen}
								/>
							</div>
						</div>
					)}

					<div className="flex flex-1">
						{/* Desktop Sidebar */}
						<aside className="hidden md:flex sm-md:hidden flex-col w-64 p-8 gap-2">
							<div className="flex items-center gap-4">
								<Logo
									src={logoSrc}
									alt={org?.name || ''}
									imageError={imageError}
									onError={() => setImageError(true)}
									onClick={() => navigate(`/dashboard?token=${token}`)}
								/>
								<div className="flex-1">
									<span className="font-semibold text-sm text-primary-badge">
										{org?.name}
									</span>
								</div>
							</div>
							<NavItemsWrapper
								navItems={navItems}
								setIsMobileMenuOpen={setIsMobileMenuOpen}
							/>
						</aside>

						{/* Main Content */}
						<div className="w-full h-full min-h-screen relative">
							<Outlet />
							<ToastContainer position="bottom-right" />
						</div>
					</div>
				</>
			) : (
				<div className="w-full">
					<Outlet />
				</div>
			)}
		</div>
	);
};
